import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { Header, Footer } from "../components/Layout";
import { EvidenceBadge, ProgressBar } from "../components/Widgets";
import { LegifranceLink } from "../components/JurisWidgets";
import { Share2, ExternalLink, MessageCircle, Linkedin, Copy, Zap, ShieldCheck, ArrowLeft } from "lucide-react";

export default function PublicDemo() {
  const { caseId } = useParams();
  const [c, setC] = useState(null);
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/demo/${caseId}`;

  useEffect(() => {
    api.get(`/cases/${caseId}`).then((r) => setC(r.data)).catch(() => setC({ error: true }));
  }, [caseId]);

  const share = (platform) => {
    const text = `📚 Regarde cette analyse juridique JurisFlash — ${url}`;
    const links = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
      native: null,
    };
    if (platform === "native" && navigator.share) {
      navigator.share({ title: "JurisFlash — Analyse juridique", url });
      return;
    }
    if (platform === "copy") {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      return;
    }
    if (links[platform]) window.open(links[platform], "_blank", "noopener,noreferrer");
  };

  if (!c) return <div className="min-h-screen flex items-center justify-center"><div className="pulse-dot"></div></div>;
  if (c.error) return <div className="min-h-screen"><Header /><div className="max-w-md mx-auto p-10">Analyse introuvable ou expirée.</div><Footer /></div>;

  const a = c.analysis || {};
  const isPro = c.mode === "pro";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Meta strip */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <Link to="/" className="text-sm text-slate-600 hover:underline flex items-center gap-1" data-testid="back-home"><ArrowLeft className="w-4 h-4" /> Accueil</Link>
          <span className="j-label ml-auto">Aperçu public · Données anonymisées</span>
          <EvidenceBadge level={a.evidence_global || "amber"} />
        </div>

        <div className="j-label mb-2 flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Analyse partagée · Read-only</div>
        <h1 className="j-h1 font-heading" data-testid="public-demo-title">
          Flash Droit — Aperçu
        </h1>

        {/* SHARE STRIP */}
        <div className="j-card mt-6 border-[#10B981]/30 bg-emerald-50/40" data-testid="share-strip">
          <div className="flex items-start gap-3 flex-wrap">
            <Share2 className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <div className="font-heading font-black text-sm">Partagez ce dossier</div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{url}</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => share("whatsapp")} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid="share-whatsapp"><MessageCircle className="w-3 h-3" /> WhatsApp</button>
              <button onClick={() => share("linkedin")} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid="share-linkedin"><Linkedin className="w-3 h-3" /> LinkedIn</button>
              <button onClick={() => share("copy")} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid="share-copy"><Copy className="w-3 h-3" /> {copied ? "Copié !" : "Copier"}</button>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-6">
            {a.situation_resume && (
              <div className="j-card">
                <h3 className="font-heading font-black text-xl text-[#1E293B] mb-3">Situation</h3>
                <p className="text-slate-700 leading-relaxed">{a.situation_resume}</p>
              </div>
            )}

            {(a.vos_droits || []).length > 0 && (
              <div className="j-card">
                <h3 className="font-heading font-black text-xl text-[#1E293B] mb-3">Vos droits & la loi</h3>
                {a.vos_droits.map((d, i) => (
                  <div key={i} className="py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-heading font-bold text-[#1E293B]">{d.titre}</span>
                      <EvidenceBadge level={d.evidence} />
                      <LegifranceLink url={d.legifrance_url} />
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{d.explication}</p>
                    <div className="text-xs text-slate-400 font-mono mt-1">{d.article}</div>
                  </div>
                ))}
              </div>
            )}

            {(a.checklist_urgence || []).length > 0 && (
              <div className="j-card" data-testid="public-checklist">
                <h3 className="font-heading font-black text-xl text-[#1E293B] mb-3">Checklist d'action</h3>
                <ol className="space-y-2 list-decimal pl-4">
                  {a.checklist_urgence.map((v, i) => <li key={i} className="text-sm text-slate-700 leading-relaxed">{v}</li>)}
                </ol>
              </div>
            )}

            {/* PII redacted notice */}
            <div className="j-card border-amber-200 bg-amber-50/60 text-sm text-amber-900" data-testid="pii-notice">
              🛡️ <b>Données anonymisées.</b> Cette page publique n'expose ni noms, ni adresses, ni informations personnelles — seules les analyses juridiques sont visibles.
            </div>
          </div>

          <aside className="space-y-6">
            {a.dossier_ready_score != null && (
              <div className="j-card">
                <ProgressBar value={a.dossier_ready_score || 0} />
              </div>
            )}
            <div className="j-card border-[#10B981]/30 bg-white">
              <div className="j-label mb-2">Envie de la même analyse ?</div>
              <p className="text-sm text-slate-700">Générez la vôtre en 47 secondes, gratuitement.</p>
              <Link to="/" className="btn-emerald w-full mt-3" data-testid="cta-try-jurisflash"><Zap className="w-4 h-4" /> Lancer mon Flash Droit</Link>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
